import { useEffect, useRef, useState } from 'react';

const SHOW_THRESHOLD_PX = 2;
const HIDE_THRESHOLD_PX = 12;
const AT_TOP_THRESHOLD_PX = 500;
const MIN_HIDE_SCROLL_PX = 30;
const VV_SHRINK_TRIGGER_PX = 5;
const VV_SHRINK_MAX_PX = 120;
const VV_SHRINK_COOLDOWN_MS = 400;
const DOWN_COOLDOWN_MS = 400;

export type ScrollDirection = 'up' | 'down' | null;

export type ScrollDirectionState = {
  isAtTop: boolean;
  scrollDirection: ScrollDirection;
};

export function useScrollDirection(): ScrollDirectionState {
  const [state, setState] = useState<ScrollDirectionState>({
    isAtTop: true,
    scrollDirection: null,
  });

  const lastScrollY = useRef(typeof window === 'undefined' ? 0 : window.scrollY);
  const accumulatedDelta = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let vvShrinkActive = false;
    let vvShrinkTimer: ReturnType<typeof setTimeout> | null = null;
    let downCooldownActive = false;
    let downCooldownTimer: ReturnType<typeof setTimeout> | null = null;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;
        lastScrollY.current = currentY;
        const isAtTop = currentY < AT_TOP_THRESHOLD_PX;

        if (delta === 0) {
          setState((prev) =>
            prev.isAtTop === isAtTop ? prev : { ...prev, isAtTop },
          );
          ticking.current = false;
          return;
        }

        const flipped =
          (delta > 1 && accumulatedDelta.current < 0) ||
          (delta < -1 && accumulatedDelta.current > 0);
        if (flipped) accumulatedDelta.current = 0;
        accumulatedDelta.current += delta;

        const canHide =
          !vvShrinkActive &&
          accumulatedDelta.current > HIDE_THRESHOLD_PX &&
          currentY > MIN_HIDE_SCROLL_PX;

        if (canHide) {
          accumulatedDelta.current = 0;
          downCooldownActive = true;
          if (downCooldownTimer) clearTimeout(downCooldownTimer);
          downCooldownTimer = setTimeout(() => {
            downCooldownActive = false;
          }, DOWN_COOLDOWN_MS);
          setState({ isAtTop, scrollDirection: 'down' });
        } else if (
          !downCooldownActive &&
          accumulatedDelta.current < -SHOW_THRESHOLD_PX
        ) {
          accumulatedDelta.current = 0;
          setState({ isAtTop, scrollDirection: 'up' });
        } else {
          setState((prev) =>
            prev.isAtTop === isAtTop ? prev : { ...prev, isAtTop },
          );
        }

        ticking.current = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    const vv = window.visualViewport;
    let lastVVHeight = vv ? vv.height : 0;
    let accumulatedShrink = 0;
    const onVVResize = () => {
      if (!vv) return;
      const h = vv.height;
      const delta = lastVVHeight - h;
      lastVVHeight = h;

      if (delta > VV_SHRINK_MAX_PX || delta < 0) {
        accumulatedShrink = 0;
        return;
      }

      vvShrinkActive = true;
      if (vvShrinkTimer) clearTimeout(vvShrinkTimer);
      vvShrinkTimer = setTimeout(() => {
        vvShrinkActive = false;
      }, VV_SHRINK_COOLDOWN_MS);

      accumulatedShrink += delta;

      if (
        accumulatedShrink > VV_SHRINK_TRIGGER_PX &&
        window.scrollY > MIN_HIDE_SCROLL_PX
      ) {
        accumulatedShrink = 0;
        accumulatedDelta.current = 0;
        setState((prev) =>
          prev.scrollDirection === 'up'
            ? prev
            : { ...prev, scrollDirection: 'up' },
        );
      }
    };
    vv?.addEventListener('resize', onVVResize);

    return () => {
      window.removeEventListener('scroll', onScroll);
      vv?.removeEventListener('resize', onVVResize);
      if (vvShrinkTimer) clearTimeout(vvShrinkTimer);
      if (downCooldownTimer) clearTimeout(downCooldownTimer);
    };
  }, []);

  return state;
}
